import React, { useCallback, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isFunction } from 'ggApp/utils/function';
import CloseSmall from 'ggApp/shared/icons/CloseSmall';
import { useNethone } from 'ggApp/modules/newCheckout/hooks';

import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import stepNames from 'ggApp/modules/newCheckout/constants/stepNames';
import { CheckoutStepsProvider } from './CheckoutStepsProvider';

import { ModalCheckout, CloseButtonWrapper, CloseButton } from './styles';

const CheckoutModal = ({
    flow,
    onOpen,
    modalRef,
    onClose,
    withBackgroundColor,
    redirectus,
    user,
}) => {
    const [width, setWidth] = useState('400px');
    const [height, setHeight] = useState('80vh');
    const [modal, setModal] = useState(null);

    const { current, cart, send, activeStore } = useContext(OrderContext);

    const { orderNumber } = cart;
    const nethone = useNethone({ orderNumber, userId: user?.id });

    useEffect(() => {
        const val = current.matches('review')
            ? { width: '800px', height: 'fit-content' }
            : { width: '400px', height: '94vh' };

        if (width !== val.width) {
            setWidth(val.width);
        }

        if (height !== val.height) {
            setHeight(val.height);
        }
    }, [current, width, height]);

    const createModalRef = useCallback(
        (newModal) => {
            setModal(newModal);

            if (isFunction(modalRef)) {
                modalRef(newModal);
            }
        },
        [modalRef],
    );

    const handleOpen = useCallback(() => {
        send('START_CHECKOUT');

        if (isFunction(onOpen)) {
            onOpen();
        }
    }, [onOpen, send]);

    const handleClosing = useCallback(() => {
        send('STOP_CHECKOUT');

        if (isFunction(onClose)) {
            onClose();
        }

        if (current.matches(stepNames.ORDER_CONFIRMATION)) {
            redirectus(`/${activeStore.code}/your-tech`, true);
        }
    }, [onClose, current, send, redirectus, activeStore.code]);

    const closeModal = useCallback(
        (payload) => {
            if (modal) {
                modal.closeModal(payload);
            }
        },
        [modal],
    );

    return (
        <div data-testid="checkout-modal">
            <ModalCheckout
                className="checkoutModal"
                modalRef={createModalRef}
                onOpen={handleOpen}
                onClose={handleClosing}
                shouldCloseOnClick={false}
                width={width}
                height={height}
                withBackgroundColor={withBackgroundColor}
            >
                <CloseButtonWrapper>
                    <CloseButton
                        onClick={closeModal}
                        icon={<CloseSmall />}
                        outlined
                        small
                        data-testid="checkout-close-button"
                    />
                </CloseButtonWrapper>

                <CheckoutStepsProvider
                    flow={flow}
                    orderNumber={orderNumber}
                    order={cart}
                    closeCheckout={closeModal}
                    nethone={nethone}
                />
            </ModalCheckout>
        </div>
    );
};

CheckoutModal.propTypes = {
    onOpen: PropTypes.func,
};

CheckoutModal.defaultProps = {
    onOpen: () => {},
};

export default CheckoutModal;
